import { useCallback, useEffect, useState } from 'react';
import {  RootState } from '../redux/store';
import { 
  setActiveConversation,
  addConversation,
  setConversations,
  appendConversations,
  setError,
  setLoading,
  setLoadingTable,
  setLoadingTablePreview,
  setDataExplorer,
  setFactDimTables,
  setTablePreview,
  setActiveProjectConversationList,
  setLoadingProjectConversations,
  setProjectConversationsError,
  clearProjectConversations,
  deleteProject,
  renameProject,
  deleteConversationFromState,
  setProjectsError
} from '../features/conversation-slice';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { useAppDispatch, useAppSelector } from 'redux/hooks';


export const useConversations = (autoLoad = false) => {
  const dispatch = useAppDispatch();
  const aiChatApiService = useAiChatApiService();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { 
    byId, 
    allIds, 
    activeConversationId, 
    loading, 
    error,conversations,
    totalCount,
    pageSize,
    currentPage,
    hasMore
  } = useAppSelector((state: RootState) => state.conversations);


  const fetchConversations = useCallback(() => {
    if (loading) return; // Prevent multiple calls
    dispatch(setLoading(true));
    aiChatApiService.getConversation(1, 20).then(
      (response: any) => {
        dispatch(setConversations(response));
        dispatch(setLoading(false));
      },
      (error) => {
        console.log(error);
        dispatch(setLoading(false));
        dispatch(setError((error as Error).message));
      }
    );
  }, [dispatch, loading, aiChatApiService]);

  const loadMoreConversations = useCallback((page: number) => {
    if (loading || isLoadingMore || !hasMore) return; // Prevent multiple simultaneous requests
    
    setIsLoadingMore(true);
    aiChatApiService.getConversation(page, 10).then(
      (response: any) => {
        dispatch(appendConversations(response));
        setIsLoadingMore(false);
      },
      (error) => {
        console.log(error);
        dispatch(setError((error as Error).message));
        setIsLoadingMore(false);
      }
    );
  }, [dispatch, loading, isLoadingMore, hasMore, aiChatApiService]);

  const fetchConversation = useCallback((conversationId: string) => {
    
    },
    [dispatch]
  );

  // Select a conversation
  const selectConversation = useCallback(
    (conversationId: string) => {
      dispatch(setActiveConversation(conversationId));
    },
    [dispatch]
  );

  const newConversation = useCallback(async (title?: string) => {
    try {
      const conversation = await aiChatApiService.createConversation(title);  
        dispatch(addConversation({
          id: conversation.conversation_id,
          title: conversation.title || 'New Conversation',
          messages: [],
          lastUpdated: new Date().toISOString()
        }));
      
      return conversation.conversation_id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [dispatch]);

  const allConversations = allIds.map(id => byId[id]);

  const activeConversation = activeConversationId ? byId[activeConversationId] : null;

  const sortedConversations = [...allConversations].sort(
    (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
  );

  useEffect(() => {
    if (autoLoad && !hasLoaded && allIds.length === 0 && !loading) {
      fetchConversations();
      setHasLoaded(true);
    }
  }, [autoLoad, hasLoaded, allIds.length, loading]);

  const loadDataExplorer = useCallback(() => {
    dispatch(setDataExplorer(true));
    dispatch(setLoadingTable(true));
    aiChatApiService.getTableList().then(
      (response: any) => {
        dispatch(setFactDimTables(response));
        dispatch(setLoadingTable(false));
      },
      (error) => {
        console.log(error);
        dispatch(setLoadingTable(false));
        dispatch(setError((error as Error).message));
      }
    );
  }, [dispatch]);

  const loadTablePreview = useCallback((table_name: string) => {
    dispatch(setDataExplorer(true));
    dispatch(setLoadingTablePreview(true));
    aiChatApiService.getTablePreview(table_name).then(
      (response: any) => {
        dispatch(setTablePreview(response));
        dispatch(setLoadingTablePreview(false));
      },
      (error) => {
        console.log(error);
        dispatch(setLoadingTablePreview(false));
        dispatch(setError((error as Error).message));
      }
    );
  }, [dispatch]);

  const closeDataExplorer = useCallback(() => {
    dispatch(setDataExplorer(false));
  }, [dispatch]);

  const deleteConversationById = useCallback(async (conversationId: string) => {
    try {
      await aiChatApiService.deleteConversation(conversationId);
      dispatch(deleteConversationFromState(conversationId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return { success: false, error: (error as Error).message };
    }
  }, [dispatch, aiChatApiService]);

  return {
    allConversations,
    sortedConversations,
    activeConversation,
    activeConversationId,
    loading,
    error,
    hasLoaded,
    conversations,
    fetchConversations,
    fetchConversation,
    selectConversation,
    newConversation,
    loadDataExplorer,
    closeDataExplorer,
    loadTablePreview,
    loadMoreConversations,
    deleteConversationById,
    // Pagination state
    totalCount,
    pageSize,
    currentPage,
    hasMore,
    isLoadingMore
  };
};

// Hook for project conversations
export const useProjectConversations = () => {
  const dispatch = useAppDispatch();
  const aiChatApiService = useAiChatApiService();
  
  const { 
    activeProjectConversationList,
    loadingProjectConversations,
    projectConversationsError,
    activeProject
  } = useAppSelector((state: RootState) => state.conversations);

  const fetchProjectConversations = useCallback(async (projectId: string) => {
    if (!projectId) return;
    
    dispatch(setLoadingProjectConversations(true));
    dispatch(clearProjectConversations());
    
    try {
      const response = await aiChatApiService.getConversationsByProjectId(projectId, 1, 20);
      dispatch(setActiveProjectConversationList(response.conversations || []));
      dispatch(setLoadingProjectConversations(false));
    } catch (error) {
      console.error('Error fetching project conversations:', error);
      dispatch(setProjectConversationsError((error as Error).message));
      dispatch(setLoadingProjectConversations(false));
    }
  }, [dispatch, aiChatApiService]);

  const clearProjectConversationList = useCallback(() => {
    dispatch(clearProjectConversations());
  }, [dispatch]);

  const deleteProjectById = useCallback(async (projectId: string) => {
    try {
      await aiChatApiService.deleteProject(projectId);
      dispatch(deleteProject(projectId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting project:', error);
      dispatch(setProjectsError((error as Error).message));
      return { success: false, error: (error as Error).message };
    }
  }, [dispatch, aiChatApiService]);

  const renameProjectById = useCallback(async (projectId: string, newName: string) => {
    try {
      await aiChatApiService.renameProject(projectId, newName);
      dispatch(renameProject({ projectId, newName }));
      return { success: true };
    } catch (error) {
      console.error('Error renaming project:', error);
      dispatch(setProjectsError((error as Error).message));
      return { success: false, error: (error as Error).message };
    }
  }, [dispatch, aiChatApiService]);

  const deleteConversationById = useCallback(async (conversationId: string) => {
    try {
      await aiChatApiService.deleteConversation(conversationId);
      dispatch(deleteConversationFromState(conversationId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return { success: false, error: (error as Error).message };
    }
  }, [dispatch, aiChatApiService]);

  return {
    activeProjectConversationList,
    loadingProjectConversations,
    projectConversationsError,
    activeProject,
    fetchProjectConversations,
    clearProjectConversationList,
    deleteProjectById,
    renameProjectById,
    deleteConversationById
  };
};

export default useConversations;